<template>
  <div id="container">
    <div
      v-if="showSteps"
      id="steps"
    >
      <div
        v-for="(step, stepIndex) in steps"
        class="step"
        :class="{'passed': currentStep > step.step }"
      >
        <div :class="currentStep >= step.step ? 'bg-primary text-white' : ''">
          {{ stepIndex + 1 }}
        </div>
        <q-icon
          :name="step.icon"
          :color="currentStep >= step.step ? 'primary' : 'gray'"
          size="42px"
        />
      </div>
    </div>
    <q-carousel
      v-if="!error && user.id"
      ref="carousel"
      :arrows="false"
      :autoplay="false"
      :quick-nav="false"
      :no-swipe="true"
      :handle-arrow-keys="false"
    >
      <q-carousel-slide v-if="!hideStepOne">
        <div>
          <bj-form
            ref="form"
            gutter-size="xs"
            :fields="fields"
            :data="payload"
            @keyup.enter.native="setPassword"
          >
            <div
              slot="start"
              class="col-12"
            >
              <h5 class="no-margin underline-title text-bold">
                Hey {{ user.first_name }}!
              </h5>
              <p class="q-ma-none q-mt-sm">
                Choose a password to your account.
              </p>
            </div>
            <div
              slot="end"
              class="col-12"
            >
              <q-btn
                no-caps
                :loading="loading"
                push
                class="full-width"
                rounded
                color="primary"
                label="Create account"
                icon-right="chevron_right"
                @click="setPassword"
              />
            </div>
          </bj-form>
        </div>
      </q-carousel-slide>
      <q-carousel-slide v-if="!hideStepTwo">
        <div>
          <h5 class="no-margin underline-title text-bold">
            Agent Addendum
          </h5>
          <iframe
            v-if="currentStep === 2 && user.addendum_contract"
            sandbox="allow-top-navigation allow-scripts allow-forms allow-same-origin"
            :src="user.addendum_contract.sign_page_url"
            class="w-100 q-mt-sm"
            style="height: 500px"
          />
        </div>
      </q-carousel-slide>
      <q-carousel-slide v-if="!hideStepThree">
        <div>
          <h5 class="no-margin underline-title text-bold">
            Setup a payout method
          </h5>
          <p class="q-ma-none q-mt-sm">
            In order for us to pay you promptly and securely, you will need to register with Stripe.
            You will be required to enter your bank account to which we will transfer your earnings.
          </p>
          <a
            :href="stripeAccountLink"
            target="_top"
          >
            <q-btn
              no-caps
              :loading="loading"
              push
              class="full-width q-mt-md"
              rounded
              color="primary"
              label="Setup a Stripe account"
              icon-right="chevron_right"
            />
          </a>
        </div>
      </q-carousel-slide>
      <q-carousel-slide>
        <div id="grid">
          <div>
            <h5 class="no-margin underline-title text-bold">
              Download the Showdigs mobile app
            </h5>
            <p class="q-ma-none q-mt-sm">
              Welcome to the team! Download our app to start earning some extra cash.
            </p>
          </div>
          <div
            v-if="$store.getters['app/settings'] && $store.getters['app/settings'].length"
            id="buttons"
          >
            <div>
              <a
                v-if="$q.platform.is.desktop || $q.platform.is.android"
                v-ripple
                :href="androidUrl"
                class="relative-position inline-block"
              >
                <img src="/statics/google-play-badge.png">
              </a>
            </div>
            <div>
              <a
                v-if="$q.platform.is.desktop || $q.platform.is.ios"
                v-ripple
                :href="iosUrl"
                class="relative-position inline-block"
              >
                <img src="/statics/app-store-badge.svg">
              </a>
            </div>
          </div>
        </div>
      </q-carousel-slide>
    </q-carousel>
    <div
      v-else-if="error"
      class="text-center"
    >
      <p>
        {{ error.message }}
      </p>
      <q-btn
        :icon="error.buttonIcon"
        :label="error.buttonText"
        :color="error.buttonColor"
        @click="error.handler"
      />
    </div>
    <q-inner-loading :visible="loading">
      <q-spinner-puff
        size="80px"
        color="primary"
      />
      <div class="q-mt-md text-primary text-bold">
        Please wait...
      </div>
    </q-inner-loading>
  </div>
</template>

<script>

export default {
  data() {
    const fields = [
      {
        name: 'password',
        type: 'password',
        label: 'Password',
        rules: ['required', 'minLength:6'],
        error: 'You must enter a password with at least 6 characters',
      },
      {
        name: 'confirm_password',
        type: 'password',
        label: 'Confirm password',
        rules: ['required', {
          name: 'sameAs',
          field: 'password',
        }],
        error: 'The passwords do not match',
      },
    ];

    return {
      steps: [],
      currentStep: 1,
      hideStepOne: false,
      hideStepTwo: false,
      hideStepThree: false,
      showSteps: true,
      androidUrl: '',
      iosUrl: '',
      stripeAccountLink: '',
      loading: true,
      error: false,
      user: {},
      fields,
      payload: {
        password: '',
        confirm_password: '',
        tos_agree: false,
        agent_tos_agree: false,
      },
    };
  },
  computed: {
    isAgent() {
      return this.user.role === this.$config.roles.AGENT;
    },
  },
  created() {
    this.init();
  },
  methods: {
    init(refresh) {
      if (this.$route.query.reload && this.$route.query.reload === 'true') {
        window.top.location.reload();
      }

      if (refresh) {
        this.loading = true;
        this.error = false;
      }

      this.getUser(this.$route.params.token).then((user) => {
        this.steps = [{
          icon: 'settings',
          step: 1,
        }];

        if (user.role === this.$config.roles.AGENT) {
          this.steps.push({
            icon: 'content_paste',
            step: 2,
          });
          this.steps.push({
            icon: 'credit_card',
            step: 3,
          });
          this.steps.push({
            icon: 'get_app',
            step: 4,
          });
          this.fields.push({
            name: 'tos_agree',
            label: 'I agree to the Showdigs <a href="https://www.showdigs.com/terms-of-use" target="_blank">Terms of Use</a>.',
            type: 'checkbox',
            rules: [{
              name: 'sameAs',
              value: true,
            }],
            html: true,
          });
        } else {
          this.showSteps = false;

          if (user.password_is_set) {
            this.error = {
              message: 'This user already registered, you can\'t set a password again.',
              buttonText: 'Go to the login page',
              buttonIcon: 'arrow_forward',
              buttonColor: 'primary',
              handler: () => {
                this.redirect('auth.login');
              },
            };
            this.loading = false;
            return;
          }
        }

        if (!user.password_is_set) {
          this.currentStep = 1;
        } else if (user.addendum_contract.status !== 'signed') {
          this.currentStep = 2;
          this.hideStepOne = true;
        } else if (!user.stripe) {
          this.loading = true;
          this.getStripeAccountLink();
          this.currentStep = 3;
          this.hideStepOne = true;
          this.hideStepTwo = true;
        } else {
          this.currentStep = 3;
          this.currentStep = 4;
          this.hideStepOne = true;
          this.hideStepTwo = true;
          this.hideStepThree = true;
        }

        this.user = user;
        this.loading = false;
      }).catch(() => {
        this.error = {
          // message: error,
          message: 'Error occurred',
          buttonText: 'Try again',
          buttonIcon: 'refresh',
          buttonColor: 'negative',
          handler: () => {
            this.init(true);
          },
        };
        this.loading = false;
      });

      this.getSettings().then((settings) => {
        settings = settings.map((setting) => ({
          key: setting.key,
          value: setting.value,
          children: setting.children.map((s) => ({
            key: s.key,
            value: s.value,
            market_id: s.market_id,
          })),
        }));
        this.$store.commit('app/SET_SETTINGS', settings);

        this.androidUrl = `https://play.google.com/store/apps/details?id=${this.getSetting('android_package_name')}`;
        this.iosUrl = `https://itunes.apple.com/us/app/apple-store/id${this.getSetting('app_store_app_id')}?ls=1&mt=8`;
      });
    },
    getUser(token) {
      return this.api().request('GET', `public/get-user-for-setting-password/${token}`);
    },
    getSettings() {
      return this.api().request('GET', 'public/settings');
    },
    setPassword() {
      if (!this.loading) {
        this.$refs.form.touch();

        this.$refs.form.validate().then(() => {
          this.loading = true;

          const payload = {
            password: this.payload.password,
            token: this.$route.params.token,
          };

          this.api().request('POST', 'auth/set-password', payload).then((user) => {
            this.loading = false;
            if (!this.isAgent) {
              this.$store.commit('auth/LOGIN', user);
              this.uiNotify(
                `Welcome ${user.full_name}`,
              );
              this.redirect('app.activities');
            } else {
              this.user = user;
              this.currentStep = 2;
              this.getStripeAccountLink();
              this.$refs.carousel.next();
            }
          }).catch((response) => {
            this.uiNotify(response.errors || 'Error occurred', 'negative');
          });
        }, () => {
          this.loading = false;
          this.uiNotify('Please enter a password', 'negative');
        });
      }
    },
    getStripeAccountLink() {
      this.api().request('POST', `stripe/agent-express-account-link/${this.$route.params.token}`).then((response) => {
        this.loading = false;
        this.stripeAccountLink = response.account_link;
      }).catch((response) => {
        this.uiNotify(response.errors || 'Error occurred', 'negative');
      });
    },
  },
};
</script>

<style lang="scss">
  @import "../../css/common";

  #container {
    width: 100%;
    height: 100%;

    > #steps {
      display: grid;
      grid-template-columns: repeat( auto-fit, minmax(1px, 1fr) );
      padding: 10px 0;
      border-radius: 3px;

      > .step {
        padding: 0 20px 5px;
        text-align: center;
        position: relative;

        --step-width: 25px;
        > div {
          width: var(--step-width);
          height: var(--step-width);
          line-height: var(--step-width);
          border-radius: 50%;
          font-weight: bold;
          background: lighten(#ccc, 10%);
          box-shadow: 0 1px 3px rgba(0,0,0,.2), 0 1px 1px rgba(0,0,0,.14), 0 2px 1px -1px rgba(0,0,0,.12);
          margin: 0 auto 8px;
        }

        &:not(:last-child) > div::after {
          content: '';
          position: absolute;
          width: calc(100% - var(--step-width));
          height: 4px;
          background: lighten(#ccc, 10%);
          top: calc(var(--step-width) / 2);
          left: calc(50% + var(--step-width) / 2);
          transform: translateY(-50%);
          z-index: 3;
        }

        &.passed > div::after {
          background: $primary-color;
        }
      }
    }

    #grid {
      height: 100%;
      display: grid;
      grid-template-rows: repeat(2, auto);
      grid-gap: 30px;
      text-align: center;

      > div:first-child {
        align-self: end;
      }

      > #buttons {
        @media only screen and (max-width: 575px) {
          grid-template-rows: repeat(2, auto);
        }

        @media only screen and (min-width: 576px) {
          grid-template-columns: repeat(2, auto);
        }

        display: grid;
        justify-content: center;
        grid-gap: 20px;

        > div > a > img {
          height: 50px;
        }
      }
    }
  }
</style>
